import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import { useListConfigs } from '../../hooks/useLeadImportApi';
import { useImportWizardStore } from '../../store/useImportWizardStore';
import type { MappingConfig } from '../../utils/types';

interface SavedConfigPickerProps {
  onConfigSelected: () => void;
}

const SavedConfigPicker: React.FC<SavedConfigPickerProps> = ({
  onConfigSelected,
}) => {
  const { data: rawData, isLoading } = useListConfigs();
  const updateMappingConfig = useImportWizardStore(
    (s) => s.updateMappingConfig
  );

  // Normalize: API may return array directly or wrapped in { configs: [...] }
  const configs = Array.isArray(rawData)
    ? rawData
    : Array.isArray((rawData as any)?.configs)
    ? (rawData as any).configs
    : [];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading saved configurations...
        </Typography>
      </Box>
    );
  }

  if (!configs || configs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Apply a saved configuration
      </Typography>
      <Autocomplete
        options={configs}
        getOptionLabel={(opt: any) =>
          opt.name || opt.config_name || 'Unnamed config'
        }
        onChange={(_e, selectedConfig: any) => {
          if (selectedConfig) {
            updateMappingConfig(
              (selectedConfig.config ||
                selectedConfig.mapping_config ||
                selectedConfig) as Partial<MappingConfig>
            );
            onConfigSelected();
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Saved Configuration"
            size="small"
            placeholder="Select a saved config..."
          />
        )}
        ListboxProps={{ style: { maxHeight: 200 } }}
        sx={{ maxWidth: 400 }}
        size="small"
        isOptionEqualToValue={(opt: any, val: any) =>
          (opt.id || opt.config_id) === (val.id || val.config_id)
        }
      />
    </Box>
  );
};

export default SavedConfigPicker;
